import { Component, computed, input } from '@angular/core';

/**
 * The five category marks the design export shipped, by name.
 *
 * The list is CLOSED and it is closed because the export is: five files under
 * `context/foundation/design-reference/stitch/icon_*`, nothing more. What is NOT closed is
 * the set of values the panel receives — see `courseAreaIcon` below — which is exactly why
 * the last entry exists as a real destination rather than as a "missing icon" placeholder.
 */
export const CATEGORY_ICON_NAMES = [
  'dydaktyczny',
  'wychowawczy',
  'prawno-organizacyjny',
  'rozwoj-osobisty',
  'inny',
] as const;

export type CategoryIconName = (typeof CATEGORY_ICON_NAMES)[number];

/**
 * Where every unknown area name lands. Not a fallback in the apologetic sense: a training
 * whose area the export never drew is still a training, and it gets a mark like every other
 * row rather than a hole in the layout.
 */
export const FALLBACK_CATEGORY_ICON: CategoryIconName = 'inny';

/**
 * `courseAreaName` → icon, spelled out.
 *
 * WHY A DICTIONARY AND NOT A COLOUR. Category is carried by the SHAPE of the mark, never by
 * a hue (Q-186/A, WCAG 1.4.1): a reader with colour vision deficiency, a monochrome printout
 * and the high-contrast mode of a later phase all keep the distinction, and none of them
 * would keep a tint.
 *
 * THE KEYS ARE MEASURED, NOT GUESSED (Q-207, Option A, 2026-08-16). Until then the table
 * carried five names copied off the design export plus TWO of the implementer's own making
 * („prawo oświatowe", „pomoc psychologiczna") — strings that live in this repository's test
 * fixtures and in no dictionary the office ever typed. The replacement is the full result of
 * `SELECT DISTINCT "name" FROM "courseAreas"` against the working copy of the production
 * database: SIX values, every one of them also in use by a course. They are listed below in
 * the order the query returned them, and „INNE" is the entry the old table was missing — it
 * had been landing on the fallback by accident rather than by decision.
 *
 * THE TABLE STILL CANNOT BE COMPLETE, and nothing here pretends otherwise: `courseAreaName`
 * comes out of a dictionary the office edits in the admin panel, so it is an OPEN set and a
 * seventh area can appear tomorrow. Everything outside this table is `inny` — a decision,
 * not an omission, and the case `icon.component.spec.ts` proves on a name no dictionary
 * carries.
 *
 * Keys are compared normalised (trimmed, lower-cased), because the stored values are
 * upper-cased („ROZWÓJ OSOBISTY") while a human retyping one would not be.
 */
const COURSE_AREA_ICONS: Readonly<Record<string, CategoryIconName>> = {
  dydaktyczny: 'dydaktyczny',
  inne: 'inny',
  inny: 'inny',
  'prawno-organizacyjny': 'prawno-organizacyjny',
  'rozwój osobisty': 'rozwoj-osobisty',
  wychowawczy: 'wychowawczy',
};

/**
 * The mark for an area name, with `null` treated exactly like an unrecognised name.
 *
 * `null` deliberately does NOT mean "no icon": a row without a mark in a list of rows with
 * marks reads as a rendering failure, and the layout would jump by the width of the tile.
 */
export function courseAreaIcon(areaName: string | null): CategoryIconName {
  if (areaName === null) {
    return FALLBACK_CATEGORY_ICON;
  }

  return COURSE_AREA_ICONS[areaName.trim().toLowerCase()] ?? FALLBACK_CATEGORY_ICON;
}

/**
 * The panel's icon primitive: inline SVG, one `currentColor` stroke, no webfont.
 *
 * NO `<mat-icon>` AND NO ICON WEBFONT, and that is measured rather than preferred. The
 * application ships no icon font at all, `verify-s04-p5.mjs` freezes the runtime
 * dependencies on EQUALITY (so a subsetting tool cannot be added), and a Material Symbols
 * ligature without its font renders as the literal word „school" on every row.
 *
 * `currentColor` IS THE WHOLE POINT of the markup below. The mark inherits whatever colour
 * its container carries, so it survives the high-contrast mode and the greyscale mode a
 * later phase of this slice adds. A mark with a baked-in hex goes dark in both — and a hex
 * anywhere under `src/app/**` also fails `verify:s22-p1`.
 *
 * DECORATIVE BY DEFAULT. Every place that renders a mark also renders the area NAME next to
 * it, so announcing the picture too would make a screen reader say the category twice. The
 * mark is therefore `aria-hidden`, and `focusable="false"` keeps IE-era SVG out of the tab
 * order.
 */
@Component({
  selector: 'app-icon',
  template: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      @switch (resolvedName()) {
        @case ('dydaktyczny') {
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        }
        @case ('wychowawczy') {
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        }
        @case ('prawno-organizacyjny') {
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        }
        @case ('rozwoj-osobisty') {
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        }
        @default {
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        }
      }
    </svg>
  `,
  styles: `
    :host {
      display: inline-flex;
      flex: none;
      /* Sized in em so a caller resizes the mark by resizing its own text — one lever
         instead of an input nobody would keep in step with the type scale. The floor is
         the scale's own smallest usable step: a caller rendering the mark next to a
         label-small would otherwise shrink it to eleven pixels of tangle. */
      width: 1.5em;
      height: 1.5em;
      min-width: var(--empiria-space-sm);
      min-height: var(--empiria-space-sm);
      color: inherit;
    }

    svg {
      width: 100%;
      height: 100%;
      /* The four attributes the export carried, moved into the stylesheet so every mark
         states them once instead of five times in the markup. */
      fill: none;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  `,
})
export class IconComponent {
  /**
   * Which mark to draw. Anything outside `CATEGORY_ICON_NAMES` draws the fallback, so a
   * name arriving from data cannot produce an empty box.
   */
  readonly name = input.required<string>();

  protected readonly resolvedName = computed<CategoryIconName>(() => {
    const candidate = this.name();

    return CATEGORY_ICON_NAMES.find((known) => known === candidate) ?? FALLBACK_CATEGORY_ICON;
  });
}

/**
 * SŁOWNIK GLIFÓW INTERFEJSU — osobny od `CATEGORY_ICON_NAMES` i to nie jest porządkowanie.
 *
 * Te dwa zbiory odpowiadają na różne pytania i mają różny kontrakt. Nazwy kategorii są
 * ODWZOROWANIEM DANYCH: przychodzą ze słownika, który biuro edytuje w panelu adminów,
 * więc zbiór jest OTWARTY i jedna nazwa (`inny`) musi zostać bez własnego rysunku, żeby
 * siódmy obszar miał gdzie wylądować. Krok 3 bramki `verify:s22-p3` mierzy dokładnie to:
 * DOKŁADNIE JEDNA zadeklarowana nazwa kategorii bez gałęzi `@case`. Glify interfejsu są
 * odwrotnością — zbiór ZAMKNIĘTY, wypisany w kodzie, bez ani jednej wartości pochodzącej
 * z danych. Wrzucenie ich do tamtej listy zapaliłoby tamtą bramkę (trzydzieści nazw bez
 * gałęzi zamiast jednej), a jej rozluźnienie skasowałoby jedyny automat pilnujący, że
 * nierozpoznany obszar dostaje znak zamiast dziury w układzie.
 *
 * SKĄD TEN ZESTAW: z makiet, ekran po ekranie. Kalendarz, zegar i pinezka stoją w wierszu
 * katalogu; kciuk, klepsydra i „✓ w kółku” są w pigułkach statusu „Moich szkoleń”; teczka,
 * plik i pobranie — na karcie „Dokumenty” i na liście certyfikatów; dzwonek i pomoc —
 * w górnym pasku; oko i oko przekreślone — przy polu hasła; strzałka wstecz i szewrony —
 * przy powrocie i paginacji.
 *
 * WSZYSTKO NA `currentColor`, tak samo jak wyżej i z tego samego powodu: glif z zaszytym
 * hexem gaśnie w trybie wysokiego kontrastu i w skali szarości, a hex pod `src/app/**`
 * i tak czerwieni `verify:s22-p1`.
 */
export const UI_ICON_NAMES = [
  'kalendarz',
  'zegar',
  'pinezka',
  'osoba',
  'grupa',
  'budynek',
  'monitor',
  'plik',
  'folder',
  'pobranie',
  'potwierdzenie',
  'odznaka',
  'kciuk',
  'klepsydra',
  'lupa',
  'filtr',
  'dzwonek',
  'pomoc',
  'banknot',
  'strzalka-wstecz',
  'chevron-lewo',
  'chevron-prawo',
  'zamknij',
  'klodka',
  'oko',
  'oko-przekreslone',
  'odswiez',
  'dostepnosc',
  'wyloguj',
  'ostrzezenie',
  'info',
] as const;

export type UiIconName = (typeof UI_ICON_NAMES)[number];

/**
 * Znak interfejsu: ten sam inline SVG i ta sama zasada `currentColor` co przy kategoriach.
 *
 * DEKORACYJNY Z ZAŁOŻENIA (`aria-hidden`), bo każde miejsce, które go rysuje, pisze obok
 * SŁOWO — pigułka statusu, etykieta wiersza, nazwa sekcji. Znak, który miałby nieść
 * znaczenie sam, wymagałby roli i nazwy dostępnej, a takiego użycia w tym repozytorium
 * nie ma i ta klasa nie jest po to, żeby je umożliwić.
 *
 * `info` NIE MA własnej gałęzi `@case` i rysuje go `@default` — dokładnie tak samo jak
 * `inny` wyżej. Typ `UiIconName` wyklucza nazwę spoza listy już na etapie kompilacji
 * szablonu (`strictTemplates`), więc `@default` jest ostatnią siatką, a nie ścieżką
 * roboczą.
 */
@Component({
  selector: 'app-ui-icon',
  template: `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      @switch (name()) {
        @case ('kalendarz') {
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        }
        @case ('zegar') {
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5.5l3.5 2" />
        }
        @case ('pinezka') {
          <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
          <circle cx="12" cy="10" r="2.5" />
        }
        @case ('osoba') {
          <circle cx="12" cy="8" r="3.75" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        }
        @case ('grupa') {
          <circle cx="9" cy="8" r="3.5" />
          <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
          <path d="M16.5 5.2a3.5 3.5 0 0 1 0 6.6M18 14.5a6.5 6.5 0 0 1 3.5 5.5" />
        }
        @case ('budynek') {
          <path d="M4 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16" />
          <path d="M15 9h3a2 2 0 0 1 2 2v10M3 21h18" />
          <path d="M8 7h3M8 11h3M8 15h3" />
        }
        @case ('monitor') {
          <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
          <path d="M8.5 20.5h7M12 16.5v4" />
        }
        @case ('plik') {
          <path d="M14 3H7.5A1.5 1.5 0 0 0 6 4.5v15A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V7z" />
          <path d="M14 3v4h4M9.5 12.5h5M9.5 16h3.5" />
        }
        @case ('folder') {
          <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.5h9A1.5 1.5 0 0 1 21 10v8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18z" />
        }
        @case ('pobranie') {
          <path d="M12 3.5v11M7.5 10 12 14.5 16.5 10" />
          <path d="M4.5 17.5v1.5A1.5 1.5 0 0 0 6 20.5h12a1.5 1.5 0 0 0 1.5-1.5v-1.5" />
        }
        @case ('potwierdzenie') {
          <circle cx="12" cy="12" r="9" />
          <path d="m8 12.3 2.8 2.7L16 9.8" />
        }
        @case ('odznaka') {
          <circle cx="12" cy="9" r="5.5" />
          <path d="m8.5 13.4-1.5 7.6 5-2.4 5 2.4-1.5-7.6" />
        }
        @case ('kciuk') {
          <path d="M7 10.5v9H4.5a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z" />
          <path d="M7 10.5 11 3a2.5 2.5 0 0 1 2.5 2.5v3.5h5A1.8 1.8 0 0 1 20.2 11l-1.4 7a1.8 1.8 0 0 1-1.8 1.5H7" />
        }
        @case ('klepsydra') {
          <path d="M6.5 3h11M6.5 21h11" />
          <path d="M7.5 3v3.5c0 2 4.5 3.7 4.5 5.5s-4.5 3.5-4.5 5.5V21" />
          <path d="M16.5 3v3.5c0 2-4.5 3.7-4.5 5.5s4.5 3.5 4.5 5.5V21" />
        }
        @case ('lupa') {
          <circle cx="11" cy="11" r="6.5" />
          <path d="m15.8 15.8 4.7 4.7" />
        }
        @case ('filtr') {
          <path d="M4 6h16M7 12h10M10 18h4" />
        }
        @case ('dzwonek') {
          <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9" />
          <path d="M10.3 19a2 2 0 0 0 3.4 0" />
        }
        @case ('pomoc') {
          <circle cx="12" cy="12" r="9" />
          <path d="M9.6 9.3a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.8-.9 1.4v.4" />
          <path d="M12 17h.01" />
        }
        @case ('banknot') {
          <rect x="2.5" y="6" width="19" height="12" rx="2" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M6 10v4M18 10v4" />
        }
        @case ('strzalka-wstecz') {
          <path d="M20 12H4M10 6 4 12l6 6" />
        }
        @case ('chevron-lewo') {
          <path d="m15 5-6 7 6 7" />
        }
        @case ('chevron-prawo') {
          <path d="m9 5 6 7-6 7" />
        }
        @case ('zamknij') {
          <path d="m6 6 12 12M18 6 6 18" />
        }
        @case ('klodka') {
          <rect x="4" y="10.5" width="16" height="10" rx="2" />
          <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
        }
        @case ('oko') {
          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
          <circle cx="12" cy="12" r="3" />
        }
        @case ('oko-przekreslone') {
          <path d="M9.9 5.8A9.3 9.3 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-3.3 4" />
          <path d="M6.3 7.7A17 17 0 0 0 2.5 12S6 18.5 12 18.5a9.4 9.4 0 0 0 3.8-.8" />
          <path d="M10 10a3 3 0 0 0 4.1 4.1M3.5 3.5l17 17" />
        }
        @case ('odswiez') {
          <path d="M20 12a8 8 0 1 1-2.4-5.7" />
          <path d="M20.5 3.5V9H15" />
        }
        @case ('dostepnosc') {
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="7.6" r="1.1" />
          <path d="M7.8 10.4h8.4M12 10.8v4M12 14.8 9.8 19M12 14.8 14.2 19" />
        }
        @case ('wyloguj') {
          <path d="M14.5 4.5H6.5A1.5 1.5 0 0 0 5 6v12a1.5 1.5 0 0 0 1.5 1.5h8" />
          <path d="M17 8.5 20.5 12 17 15.5M20.5 12H10" />
        }
        @case ('ostrzezenie') {
          <path d="M12 3.8 21 19.5H3z" />
          <path d="M12 9.8v4.4M12 17h.01" />
        }
        @default {
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5.5M12 7.6h.01" />
        }
      }
    </svg>
  `,
  styles: `
    :host {
      display: inline-flex;
      flex: none;
      /* Ta sama miara i to samo uzasadnienie co przy znaku kategorii: rozmiar bierze się
         ze stopnia pisma wołającego, a nie z wejścia, którego nikt nie utrzyma w zgodzie
         ze skalą. */
      width: 1.25em;
      height: 1.25em;
      min-width: var(--empiria-space-sm);
      min-height: var(--empiria-space-sm);
      color: inherit;
    }

    svg {
      width: 100%;
      height: 100%;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.75;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  `,
})
export class UiIconComponent {
  /** Który glif narysować. Typ zamyka zbiór — nazwa spoza listy nie skompiluje szablonu. */
  readonly name = input.required<UiIconName>();
}
